// Карта «где суда замолкают»: ячейки 0.5° по точке НАЧАЛА молчания.
//
// Точка начала, а не середина провала: где судно в последний раз было видно —
// факт, а середина — интерполяция по хорде, то есть догадка. Раздел описательный,
// и на карту попадает только измеренное.
//
// Заливка — от синего к янтарю по часам молчания, без «зелёный→красный»:
// светофор читался бы как оценка поведения, а мы фиксируем факт.

import { domainMaxBounds } from '../domain/limits'
import { useEffect, useMemo, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import type { ObsCell } from '../api'
import { t } from '../i18n'
import { baseStyle } from '../map/baseStyle'
import { registerBasemapMap } from '../map/basemapRefresh'
import { createStyleGate, type StyleGate } from '../map/styleGate'
import { blindHoursRamp, cellsToGeoJSON } from './obsCells'
import { formatHours } from './obsFormat'
import { BasemapAttribution } from '../map/attribution/BasemapAttribution'
import { BoundaryToggles } from '../map/BoundaryToggles'
import { addUnderBoundaries } from '../map/layerOrder'
import { boxKey, boundsToBox, useBoundaryOverlays, type BoundaryToggles as Toggles } from '../map/useBoundaryOverlays'
import type { BBox } from '../api'

const SOURCE_ID = 'obs-cells'
const FILL_ID = 'obs-cells-fill'
const LINE_ID = 'obs-cells-line'

// Дальневосточный промысловый район — то, ради чего раздел существует. Данные
// шире (в выгрузке есть Мурманск и Норвегия), но стартовый вид не должен
// заставлять искать свой флот.
const DEFAULT_CENTER: [number, number] = [148, 50]
const DEFAULT_ZOOM = 3.2

export function ObsMap({ cells }: { cells: ObsCell[] }) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const gateRef = useRef<StyleGate | null>(null)
  // Машина без WebGL (старое железо, удалённый рабочий стол, headless-браузер)
  // не должна ронять ВЕСЬ раздел: MapLibre бросает из конструктора, и без этого
  // флага исключение уносит всё дерево React, включая цифры и таблицы.
  const [glFailed, setGlFailed] = useState(false)

  // Рубежи (просьба владельца 19.09.2026: «на всех картах»): где суда
  // замолкают, читают против промысловых районов и границ ИЭЗ. В адрес не
  // пишутся — карта здесь врезка раздела, а не свой вид.
  const [boundaries, setBoundaries] = useState<Toggles>({ eez: false, districts: false })
  const [box, setBox] = useState<BBox | null>(null)
  const boundaryMeta = useBoundaryOverlays({
    mapRef, gateRef, on: boundaries, viewKey: boxKey(box),
  })

  const data = useMemo(() => cellsToGeoJSON(cells), [cells])
  const ramp = useMemo(() => blindHoursRamp(cells), [cells])

  useEffect(() => {
    if (!containerRef.current || mapRef.current || glFailed) return
    let map: maplibregl.Map
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: baseStyle(),
        // Подпись рисуем сами: встроенный блок MapLibre пишет своё имя в тот же
        // угол и лезет под знак поставщика — см. attributionControl.test.ts
        attributionControl: false,
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        // Рамка домена: карта не уводится туда, где сервер откажет.
        maxBounds: domainMaxBounds(),
      })
    } catch {
      setGlFailed(true)
      return
    }
    mapRef.current = map
    const gate = createStyleGate()
    gateRef.current = gate
    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
    map.keyboard.disableRotation()
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')

    map.on('load', () => {
      gate.markReady()
      setBox(boundsToBox(map))
    })
    map.on('moveend', () => setBox(boundsToBox(map)))

    const popup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'obs-popup',
    })
    map.on('mousemove', FILL_ID, e => {
      const props = e.features?.[0]?.properties as
        | { gaps: number; vessels: number; blind_hours: number }
        | undefined
      if (!props) return
      map.getCanvas().style.cursor = 'pointer'
      popup
        .setLngLat(e.lngLat)
        .setHTML(
          `<div style="font-size:11px;line-height:1.5;white-space:nowrap">` +
            `${t('obs.map.silence')}: <b>${formatHours(Number(props.blind_hours))}</b><br/>` +
            `${t('obs.gaps')}: ${props.gaps}<br/>${t('obs.vessels')}: ${props.vessels}</div>`,
        )
        .addTo(map)
    })
    map.on('mouseleave', FILL_ID, () => {
      map.getCanvas().style.cursor = ''
      popup.remove()
    })

    // Смена языка кабинета меняет АДРЕС тайла подложки (у Яндекса `lang=`),
    // а карта строится один раз — учёт довозит новый адрес до неё.
    const unregisterBasemap = registerBasemapMap(map)
    return () => {
      unregisterBasemap()
      map.remove()
      mapRef.current = null
      gateRef.current = null
    }
  }, [glFailed])

  // Данные и шкала приходят асинхронно — кладём через шлюз стиля: событие `load`
  // одноразовое, и без шлюза первая порция данных терялась бы навсегда.
  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate) return
    gate.run(() => {
      const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined
      if (source) {
        source.setData(data as unknown as GeoJSON.FeatureCollection)
      } else {
        map.addSource(SOURCE_ID, {
          type: 'geojson',
          data: data as unknown as GeoJSON.FeatureCollection,
        })
        // Ячейки — картинка, рубежи лежат поверх (см. layerOrder).
        addUnderBoundaries(map, {
          id: FILL_ID,
          type: 'fill',
          source: SOURCE_ID,
          paint: { 'fill-opacity': 0.65 },
        })
        addUnderBoundaries(map, {
          id: LINE_ID,
          type: 'line',
          source: SOURCE_ID,
          paint: { 'line-color': '#0b1f2a', 'line-width': 0.3, 'line-opacity': 0.4 },
        })
      }
      map.setPaintProperty(FILL_ID, 'fill-color', [
        'interpolate',
        ['linear'],
        ['get', 'blind_hours'],
        ...ramp.stops.flat(),
      ] as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>)
    })
  }, [data, ramp])

  if (glFailed) {
    return (
      <div className="flex h-32 items-center justify-center rounded-lg border border-hull px-4 text-center text-xs text-mist">
        {t('obs.map.noWebgl')}
      </div>
    )
  }

  return (
    <div className="relative h-72 overflow-hidden rounded-lg border border-hull">
      <div ref={containerRef} className="h-full w-full" />
      {/* Подпись подложки — требование условий поставщика: у Яндекса знак
          обязан стоять в углу карты и вести на Яндекс Карты, у Carto
          обязана быть видна атрибуция OSM и CARTO. */}
      <BasemapAttribution corner="br" />
      <div className="absolute left-2 top-2 max-w-[220px] rounded border border-hull bg-abyss/85 px-2 py-1.5 text-[11px] backdrop-blur">
        <BoundaryToggles
          on={boundaries}
          onToggle={key => setBoundaries(s => ({ ...s, [key]: !s[key] }))}
          meta={boundaryMeta}
        />
      </div>
      {/* Легенда показывает РЕАЛЬНЫЕ границы цветов: шкала идёт по квантилям,
          и «0 … максимум» вводило бы в заблуждение — середина шкалы лежит
          далеко не посередине диапазона. */}
      <div className="pointer-events-none absolute bottom-2 left-2 rounded border border-hull bg-abyss/85 px-2 py-1.5 text-[10px] text-mist backdrop-blur">
        <div className="mb-1">{t('obs.map.legend')}</div>
        <div className="flex items-center gap-1">
          {ramp.stops.map(([value, color], i) => (
            <span key={value} className="flex items-center gap-1">
              <span
                className="inline-block h-2.5 w-4 rounded-sm"
                style={{ backgroundColor: color }}
              />
              <span className="tabular-nums">
                {i === ramp.stops.length - 1 ? formatHours(ramp.max) : formatHours(value)}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}
